import React from "react";
import styled from "styled-components";
import {
  ElementContainer,
  FlexibleContainer,
  RowContainer,
} from "../containers/styledComponents";
import { CompassParagraph } from "../common/styledComponents";
import { Icon, IconContainer } from "./styledComponents";
import { compassLightGreen, compassBordeaux, greyText } from "../../styles";
import { Tooltip } from "antd";
import votingIcon from "../../assets/svg/notification.svg";
import {
  InteractionClass,
  LinkageType,
  SequenceFlow,
  INTERACTION_CLASS_DISPLAY,
  LINKAGE_TYPE_DISPLAY,
  SEQUENCE_FLOW_DISPLAY,
} from "../../models/engagementVotingMappingModels";
import { formatPct } from "../../utils/formattingUtils";
import { Avatar } from "./Avatar";
import aiBadge from "../../assets/svg/ai_logo.svg";

const BadgeContainer = styled(FlexibleContainer)<{ isConsistent?: boolean }>`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ isConsistent }): string =>
      isConsistent ? compassLightGreen : compassBordeaux};

  max-width: 280px;
`;

const ConfidenceBadge = styled.div<{ score: number }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  background: ${({ score }): string =>
    score >= 0.8
      ? compassLightGreen
      : score >= 0.5
      ? "#FFC600"
      : compassBordeaux};
`;

const TooltipContent = styled.div`
  padding: 8px;
`;

interface EngagementVotingBadgeProps {
  votingRecordId: string;
  confidenceScore: number;
  interactionClass: InteractionClass;
  linkageType: LinkageType;
  sequenceFlow: SequenceFlow;
  matchingFactors: string;
  rationale: string;
}

export const EngagementVotingBadge: React.FC<EngagementVotingBadgeProps> = ({
  votingRecordId,
  confidenceScore,
  interactionClass,
  linkageType,
  sequenceFlow,
  matchingFactors,
  rationale,
}) => {
  const isConsistent =
    interactionClass === InteractionClass.CONSISTENT_REINFORCEMENT;

  const tooltipContent = (
    <TooltipContent>
      <CompassParagraph fontSize={12} bold={true} mb={4}>
        Voting Record: {votingRecordId.substring(0, 30)}...
      </CompassParagraph>
      <CompassParagraph fontSize={11} mb={4}>
        <strong>Linkage:</strong> {LINKAGE_TYPE_DISPLAY[linkageType]}
      </CompassParagraph>
      <CompassParagraph fontSize={11} mb={4}>
        <strong>Sequence:</strong> {SEQUENCE_FLOW_DISPLAY[sequenceFlow]}
      </CompassParagraph>
      <CompassParagraph fontSize={11} mb={4}>
        <strong>Matching Factors:</strong> {matchingFactors}
      </CompassParagraph>
      <CompassParagraph fontSize={11}>
        <strong>Rationale:</strong> {rationale.substring(0, 200)}
        {rationale.length > 200 ? "..." : ""}
      </CompassParagraph>
    </TooltipContent>
  );

  return (
    <Tooltip title={tooltipContent} placement="bottom">
      <BadgeContainer
        isConsistent={isConsistent}
        ai="center"
        jc="space-between"
        fd="row"
      >
        <RowContainer>
          <Avatar username={"AI Generated"} image={aiBadge} />
        </RowContainer>
        <ElementContainer mr={8}>
          <IconContainer size={20} p={0}>
            <Icon src={votingIcon} alt="Voting" />
          </IconContainer>
        </ElementContainer>
        <FlexibleContainer fd="column" ai="flex-start" fg={1}>
          <CompassParagraph fontSize={11} bold={true}>
            {INTERACTION_CLASS_DISPLAY[interactionClass]}
          </CompassParagraph>
          <CompassParagraph fontSize={10} color={greyText}>
            {SEQUENCE_FLOW_DISPLAY[sequenceFlow]}
          </CompassParagraph>
        </FlexibleContainer>
        <ElementContainer ml={8}>
          <ConfidenceBadge score={confidenceScore}>
            {formatPct(confidenceScore, true)}
          </ConfidenceBadge>
        </ElementContainer>
      </BadgeContainer>
    </Tooltip>
  );
};

interface EngagementVotingBadgeListProps {
  mappings: Array<{
    votingRecordId: string;
    confidenceScore: number;
    interactionClass: InteractionClass;
    linkageType: LinkageType;
    sequenceFlow: SequenceFlow;
    matchingFactors: string;
    rationale: string;
  }>;
}

export const EngagementVotingBadgeList: React.FC<
  EngagementVotingBadgeListProps
> = ({ mappings }) => {
  if (!mappings || mappings.length === 0) {
    return null;
  }

  return (
    <FlexibleContainer fw="wrap" jc="flex-start" ai="flex-start">
      {mappings.map((mapping, index) => (
        <ElementContainer key={index} mr={8} mb={8}>
          <EngagementVotingBadge
            votingRecordId={mapping.votingRecordId}
            confidenceScore={mapping.confidenceScore}
            interactionClass={mapping.interactionClass}
            linkageType={mapping.linkageType}
            sequenceFlow={mapping.sequenceFlow}
            matchingFactors={mapping.matchingFactors}
            rationale={mapping.rationale}
          />
        </ElementContainer>
      ))}
    </FlexibleContainer>
  );
};
